import React, { useState, useEffect } from 'react';
import {
  RefreshCw, ShoppingBag, Clock, CheckCircle, XCircle,
  X, Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  Package, MapPin,
} from 'lucide-react';
import { apiService } from '../../api/api';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
const ORDER_STATUSES = {
  1: { label: 'Pending',  dot: '#f59e0b', color: '#92400e' },
  2: { label: 'Approved', dot: '#16a34a', color: '#166534' },
  3: { label: 'Rejected', dot: '#dc2626', color: '#991b1b' },
};

const PAYMENT_LABELS = {
  0: 'COD',
  1: 'Online',
  2: 'Card',
  3: 'UPI',
};

const getStatusInfo  = (id) => ORDER_STATUSES[parseInt(id)] || ORDER_STATUSES[1];
const getStatusLabel = (id) => getStatusInfo(id).label;
const getPayment     = (id) => PAYMENT_LABELS[parseInt(id)] ?? `#${id}`;

// ─────────────────────────────────────────────────────────────
// StatusBadge
// ─────────────────────────────────────────────────────────────
const StatusBadge = ({ statusId }) => {
  const s = getStatusInfo(statusId);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 13, fontWeight: 600, color: s.color,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: s.dot, flexShrink: 0,
        display: 'inline-block',
      }} />
      {s.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// AddressCard — renders one address block
// ─────────────────────────────────────────────────────────────
const AddressCard = ({ label, address }) => {
  if (!address) return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>Not available</div>
    </div>
  );

  const lines = [
    address.fullName,
    address.mobileNo && `📞 ${address.mobileNo}`,
    address.addressLine1,
    address.addressLine2,
    address.landmark && `Near ${address.landmark}`,
    [address.city, address.state, address.pincode].filter(Boolean).join(', '),
    address.country,
  ].filter(Boolean);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{label}</div>
      {lines.map((line, i) => (
        <div key={i} style={{ fontSize: 13, color: i === 0 ? '#1a202c' : '#4b5563', fontWeight: i === 0 ? 600 : 400, lineHeight: 1.6 }}>
          {line}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Order Detail Modal
// ─────────────────────────────────────────────────────────────
const OrderDetailModal = ({ order, onClose }) => {
  const [orderItems,    setOrderItems]    = useState([]);
  const [itemsLoading,  setItemsLoading]  = useState(false);
  const [itemsError,    setItemsError]    = useState(null);

  const [allAddresses,      setAllAddresses]      = useState([]);
  const [addressesLoading,  setAddressesLoading]  = useState(false);
  const [addressesError,    setAddressesError]    = useState(null);

  // Fetch order items
  useEffect(() => {
    if (!order) return;
    const fetchItems = async () => {
      setItemsLoading(true);
      setItemsError(null);
      try {
        const userId = order.userId || order.UserId || 0;
        const items  = await apiService.getOrderItems(order.id, userId);
        setOrderItems(Array.isArray(items) ? items : []);
      } catch (err) {
        setItemsError('Could not load order items.');
      } finally {
        setItemsLoading(false);
      }
    };
    fetchItems();
  }, [order?.id]);

  // Fetch addresses for the order's user
  useEffect(() => {
    if (!order) return;
    const fetchAddresses = async () => {
      setAddressesLoading(true);
      setAddressesError(null);
      try {
        const userId = order.userId || order.UserId || 0;
        const addrs  = await apiService.getUserAddresses(userId);
        setAllAddresses(Array.isArray(addrs) ? addrs : []);
      } catch (err) {
        setAddressesError('Could not load addresses.');
      } finally {
        setAddressesLoading(false);
      }
    };
    fetchAddresses();
  }, [order?.id]);

  if (!order) return null;

  // Match billing / shipping address by ID stored on the order
  const billingAddressId  = parseInt(order.billingAddressId  || order.BillingAddressId  || 0);
  const shippingAddressId = parseInt(order.shippingAddressId || order.ShippingAddressId || 0);

  const billingAddress  = allAddresses.find(a => parseInt(a.id || a.Id) === billingAddressId)
                       || allAddresses.find(a => a.isBilling  || a.IsBilling)
                       || null;
  const shippingAddress = allAddresses.find(a => parseInt(a.id || a.Id) === shippingAddressId)
                       || allAddresses.find(a => a.isShipping || a.IsShipping)
                       || null;

  const rows = [
    { label: 'Order No.',  value: order.orderNumber },
    { label: 'Customer',   value: order.customerName },
    { label: 'Order Date', value: order.orderDateFmt },
    { label: 'Payment',    value: getPayment(order.paymentMethod) },
    { label: 'Sub Total',  value: `$${parseFloat(order.subTotal       || 0).toFixed(2)}` },
    { label: 'Tax',        value: `$${parseFloat(order.taxAmount      || 0).toFixed(2)}` },
    { label: 'Shipping',   value: `$${parseFloat(order.shippingAmount || 0).toFixed(2)}` },
    { label: 'Discount',   value: `$${parseFloat(order.discountAmount || 0).toFixed(2)}` },
    { label: 'Remarks', value: (order.remarks && order.remarks.trim() !== '' && order.remarks.toLowerCase() !== 'string') ? order.remarks : null, full: true },
  ];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#018d71 0%,#018d71 100%)', padding: '22px 24px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, zIndex: 1 }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Order Details</div>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>{order.orderNumber}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', flexShrink: 0 }}>
            <X size={16} />
          </button>
        </div>

        {/* Current status */}
        <div style={{ padding: '18px 24px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Current Status</span>
          <StatusBadge statusId={order.orderStatus} />
        </div>

        {/* Grand total */}
        <div style={{ margin: '14px 24px', background: 'linear-gradient(135deg,#f0f4ff,#faf5ff)', borderRadius: 10, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e0e7ff' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#6366f1' }}>Grand Total</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#4361ee' }}>${order.grandTotal}</span>
        </div>

        {/* Info grid */}
        <div style={{ padding: '0 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
{rows.filter(({ value }) => value !== null).map(({ label, value, full }) => (
  <div key={label} style={{ gridColumn: full ? '1 / -1' : undefined }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500, color: '#1a202c' }}>{value}</div>
  </div>
))}
        </div>

        {/* ── Addresses ── */}
        <div style={{ margin: '20px 24px 0', padding: '16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <MapPin size={15} color="#4361ee" />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Addresses
            </span>
          </div>

          {addressesLoading && (
            <div style={{ textAlign: 'center', padding: '16px 0', color: '#9ca3af', fontSize: 13 }}>
              <div style={{ display: 'inline-block', width: 18, height: 18, border: '3px solid #e2e8f0', borderTopColor: '#4361ee', borderRadius: '50%', animation: 'spin 0.9s linear infinite', marginRight: 8, verticalAlign: 'middle' }} />
              Loading addresses...
            </div>
          )}

          {!addressesLoading && addressesError && (
            <div style={{ color: '#dc2626', fontSize: 13, padding: '8px 0' }}>{addressesError}</div>
          )}

          {!addressesLoading && !addressesError && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {/* Billing */}
              <div style={{ flex: '1 1 200px', background: '#fff', borderRadius: 8, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#4361ee', padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Billing
                  </span>
                </div>
                <AddressCard label="" address={billingAddress} />
              </div>

              {/* Shipping */}
              <div style={{ flex: '1 1 200px', background: '#fff', borderRadius: 8, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#018d71', padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Shipping
                  </span>
                </div>
                <AddressCard label="" address={shippingAddress} />
              </div>
            </div>
          )}
        </div>

        {/* ── Ordered Products ── */}
        <div style={{ margin: '16px 24px 24px', padding: '16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Package size={15} color="#4361ee" />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Ordered Products
            </span>
          </div>

          {itemsLoading && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13 }}>
              <div style={{ display: 'inline-block', width: 20, height: 20, border: '3px solid #e2e8f0', borderTopColor: '#4361ee', borderRadius: '50%', animation: 'spin 0.9s linear infinite', marginRight: 8, verticalAlign: 'middle' }} />
              Loading products...
            </div>
          )}

          {!itemsLoading && itemsError && (
            <div style={{ color: '#dc2626', fontSize: 13, padding: '10px 0' }}>{itemsError}</div>
          )}

          {!itemsLoading && !itemsError && orderItems.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
              No products found for this order.
            </div>
          )}

          {!itemsLoading && !itemsError && orderItems.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {orderItems.map((item, idx) => {
                const name      = item.productName || item.name || item.ProductName || `Product #${item.productId}`;
                const sku       = item.sku || item.SKU || item.Sku || '—';
                const qty       = item.quantity || item.Quantity || 1;
                const unitPrice = parseFloat(item.unitPrice || item.UnitPrice || 0).toFixed(2);
                const total     = (parseFloat(unitPrice) * parseInt(qty)).toFixed(2);
                const imgUrl    = item.imageUrl || item.product?.imageUrl || item.Image || null;

                return (
                  <div key={item.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', borderRadius: 8, padding: '10px 12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: '#f0f4ff', border: '1px solid #e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                      {imgUrl
                        ? <img src={imgUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display='none'; }} />
                        : <ShoppingBag size={18} color="#a5b4fc" />
                      }
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a202c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>SKU: {sku}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a202c' }}>${total}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{qty} × ${unitPrice}</div>
                    </div>
                  </div>
                );
              })}

              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingTop: 6, borderTop: '1px dashed #e2e8f0', marginTop: 2 }}>
                <span style={{ fontSize: 12, color: '#6b7280', marginRight: 8 }}>{orderItems.length} item{orderItems.length !== 1 ? 's' : ''}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#4361ee' }}>
                  ${orderItems.reduce((sum, item) => sum + (parseFloat(item.unitPrice || 0) * parseInt(item.quantity || 1)), 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const OrdersPage = () => {
  const [orders,        setOrders]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [searchText,    setSearchText]    = useState('');
  const [sortField,     setSortField]     = useState('id');
  const [sortDir,       setSortDir]       = useState('desc');
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const stats = {
    total:    orders.length,
    pending:  orders.filter(o => parseInt(o.orderStatus) === 1).length,
    approved: orders.filter(o => parseInt(o.orderStatus) === 2).length,
    rejected: orders.filter(o => parseInt(o.orderStatus) === 3).length,
  };

  useEffect(() => { fetchOrders(); }, []);

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchOrders = async () => {
    setLoading(true); setError(null);
    try {
      const [ordersRes, usersRes] = await Promise.all([
        apiService.get('/Orders'),
        apiService.getAllUsers().catch(() => []),
      ]);

      const raw =
        Array.isArray(ordersRes)       ? ordersRes :
        Array.isArray(ordersRes?.data) ? ordersRes.data : [];

      const usersArr =
        Array.isArray(usersRes)        ? usersRes :
        Array.isArray(usersRes?.data)  ? usersRes.data :
        Array.isArray(usersRes?.users) ? usersRes.users : [];

      const userMap = new Map();
      usersArr.forEach(u => {
        const uid = u.id || u.userId || u.ID;
        if (uid) userMap.set(parseInt(uid), u.name || u.Name || `User #${uid}`);
      });

      const sorted = [...raw].sort((a, b) => (a.id || 0) - (b.id || 0));

      const enriched = sorted.map((order, index) => {
        const uid = parseInt(order.userId || order.UserId || 0);
        const grandTotal = (
          parseFloat(order.subTotal       || 0) +
          parseFloat(order.taxAmount      || 0) +
          parseFloat(order.shippingAmount || 0) -
          parseFloat(order.discountAmount || 0)
        ).toFixed(2);

        return {
          ...order,
          customerName: userMap.get(uid) || `User #${uid}`,
          grandTotal,
          orderNumber:  `#${String(index + 1).padStart(4, '0')}`,
          statusLabel:  getStatusLabel(order.orderStatus ?? 1),
          orderDateFmt: (order.orderDate || order.createdDate)
            ? new Date(order.orderDate || order.createdDate)
                .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : '—',
          orderTs: new Date(order.orderDate || order.createdDate || 0).getTime(),
        };
      });

      setOrders(enriched);
    } catch (err) {
      setError('Failed to load orders. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ── Filter + Search + Sort ────────────────────────────────────────────
  const processed = React.useMemo(() => {
    let result = [...orders];

    if (statusFilter !== 'all') {
      result = result.filter(o => parseInt(o.orderStatus) === parseInt(statusFilter));
    }

const qRaw = searchText.trim().toLowerCase();
const q = qRaw.replace(/[#,$₹\s]/g, '');

if (qRaw) {
  result = result.filter((o) => {
    const amount = parseFloat(o.grandTotal || 0);

    // ✅ Universal search (ALL fields)
    const matchesAllFields = Object.values(o).some((value) => {
      if (value === null || value === undefined) return false;

      if (typeof value === "number") {
        return value.toString().includes(q);
      }

      if (typeof value === "string") {
        return value.toLowerCase().includes(qRaw);
      }

      return false;
    });

    // ✅ Amount search (important 🔥)
    const amountMatch =
      amount.toString().includes(q) ||
      amount.toFixed(2).includes(q) ||
      `$${amount.toFixed(2)}`.includes(qRaw);

    // ✅ ID search (#0001 or 1)
    const idMatch =
      String(o.id).includes(q) ||
      o.orderNumber?.replace('#', '').includes(q);

    return matchesAllFields || amountMatch || idMatch;
  });
}

    result.sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (sortField === 'grandTotal') { av = parseFloat(av); bv = parseFloat(bv); }
      if (sortField === 'id' || sortField === 'orderTs') { av = Number(av); bv = Number(bv); }
      if (av < bv) return sortDir === 'asc' ? -1 :  1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });

    return result;
  }, [orders, statusFilter, searchText, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [statusFilter, searchText, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
  const paginated  = processed.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp size={12} style={{ color: '#d1d5db' }} />;
    return sortDir === 'asc'
      ? <ChevronUp   size={12} style={{ color: '#4361ee' }} />
      : <ChevronDown size={12} style={{ color: '#4361ee' }} />;
  };

  // ─────────────────────────────────────────────────────────────
  // LOADING
  // ─────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="admin-content">
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ display: 'inline-block', width: 48, height: 48, border: '4px solid #e2e8f0', borderTopColor: '#4361ee', borderRadius: '50%', animation: 'spin 0.9s linear infinite', marginBottom: 18 }} />
        <div style={{ fontSize: 16, color: '#718096', fontWeight: 500 }}>Loading orders...</div>
        <div style={{ fontSize: 13, color: '#a0aec0', marginTop: 6 }}>Fetching customer details...</div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // ERROR
  // ─────────────────────────────────────────────────────────────
  if (error) return (
    <div className="admin-content">
      <div style={{ textAlign: 'center', padding: 48, background: '#fee2e2', borderRadius: 12, border: '1px solid #fecaca', maxWidth: 480, margin: '40px auto' }}>
        <XCircle size={48} color="#dc2626" style={{ marginBottom: 12 }} />
        <h3 style={{ color: '#dc2626', marginBottom: 8 }}>Failed to Load Orders</h3>
        <p style={{ color: '#991b1b', marginBottom: 20, fontSize: 14 }}>{error}</p>
        <button onClick={fetchOrders}
          style={{ padding: '9px 22px', background: '#018d71', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <RefreshCw size={15} /> Retry
        </button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // MAIN
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="admin-content">

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1a202c' }}>Orders</h1>
          <p style={{ margin: '4px 0 0', color: '#718096', fontSize: 14 }}>Manage and track all customer orders</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={fetchOrders}
            style={{ padding: '9px 18px', background: '#018d71', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600, fontSize: 14 }}>
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Total Orders', value: stats.total,    color: '#4361ee', icon: <ShoppingBag size={20} />, bg: '#eff6ff', border: '#bfdbfe' },
          { label: 'Pending',      value: stats.pending,  color: '#f59e0b', icon: <Clock       size={20} />, bg: '#fffbeb', border: '#fde68a' },
          { label: 'Approved',     value: stats.approved, color: '#16a34a', icon: <CheckCircle size={20} />, bg: '#f0fdf4', border: '#bbf7d0' },
          { label: 'Rejected',     value: stats.rejected, color: '#dc2626', icon: <XCircle     size={20} />, bg: '#fef2f2', border: '#fecaca' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `4px solid ${s.color}`, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: s.bg, border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color, flexShrink: 0 }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#1a202c', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#718096', marginTop: 4, fontWeight: 500 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All Orders', count: stats.total,    activeColor: '#356febe1' },
            { key: '1',   label: 'Pending',    count: stats.pending,  activeColor: '#ffcf7dcc' },
            { key: '2',   label: 'Approved',   count: stats.approved, activeColor: '#16a34a88' },
            { key: '3',   label: 'Rejected',   count: stats.rejected, activeColor: '#dc262691' },
          ].map(tab => {
            const active = statusFilter === tab.key;
            return (
              <button key={tab.key} onClick={() => setStatusFilter(tab.key)} style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: active ? tab.activeColor : '#f8fafc',
                color:      active ? '#030303'           : '#4a5568',
                border:     active ? `2px solid ${tab.activeColor}` : '2px solid #e2e8f0',
                transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 7,
              }}>
                {tab.label}
                <span style={{
                  padding: '1px 7px', borderRadius: 10, fontSize: 11,
                  background: active ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                  color:      active ? '#000000' : '#718096',
                }}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', width: 280 }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search orders…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: '100%', padding: '9px 32px 9px 34px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff', color: '#1a202c' }}
            onFocus={e  => (e.target.style.borderColor = '#4361ee')}
            onBlur={e   => (e.target.style.borderColor = '#e2e8f0')}
          />
          {searchText && (
            <button onClick={() => setSearchText('')} style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, display: 'flex' }}>
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', overflow: 'hidden', border: '1px solid #f0f0f0' }}>

        {/* Per-page + result count */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#718096' }}>
            Showing{' '}
            <strong style={{ color: '#1a202c' }}>
              {Math.min((page - 1) * pageSize + 1, processed.length)}–{Math.min(page * pageSize, processed.length)}
            </strong>{' '}
            of <strong style={{ color: '#1a202c' }}>{processed.length}</strong> orders
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#718096' }}>Per page:</span>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              style={{ padding: '5px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#fff', color: '#374151' }}>
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {processed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <ShoppingBag size={50} color="#cbd5e0" style={{ marginBottom: 12 }} />
            <h3 style={{ color: '#4a5568', margin: '0 0 6px', fontSize: 17 }}>No Orders Found</h3>
            <p style={{ color: '#718096', fontSize: 14, margin: 0 }}>
              {statusFilter !== 'all'
                ? `No ${getStatusLabel(statusFilter)} orders match your search.`
                : 'No orders match your search.'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {[
                    { label: 'Order No.', field: 'id'          },
                    { label: 'Customer',  field: 'customerName'},
                    { label: 'Date',      field: 'orderTs'     },
                    { label: 'Amount',    field: 'grandTotal'  },
                    { label: 'Status',    field: 'statusLabel' },
                    { label: 'Payment',   field: null          },
                    { label: 'Action',    field: null          },
                  ].map(col => (
                    <th key={col.label}
                      onClick={() => col.field && toggleSort(col.field)}
                      style={{
                        padding: '12px 16px', textAlign: 'left', fontSize: 11,
                        fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
                        letterSpacing: '0.06em', cursor: col.field ? 'pointer' : 'default',
                        userSelect: 'none', whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {col.label}
                        {col.field && <SortIcon field={col.field} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((order, idx) => (
                  <tr
                    key={order.id || idx}
                    style={{ borderBottom: '1px solid #f7f7f7', background: '#fff', transition: 'background 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fafbff')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontWeight: 600, color: '#000000', fontSize: 13 }}>{order.orderNumber}</span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#1a202c' }}>{order.customerName}</span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ color: '#718096', fontSize: 13 }}>{order.orderDateFmt}</span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ color: '#1a202c', fontSize: 14 }}>${order.grandTotal}</span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <StatusBadge statusId={order.orderStatus ?? 1} />
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        fontSize: 13, fontWeight: 500,
                        color: order.paymentMethod === 1 ? '#3b82f6' : '#374151',
                      }}>
                        {getPayment(order.paymentMethod)}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <button
                        onClick={() => setSelectedOrder(order)}
                        style={{ padding: '6px 18px', background: '#fff', color: '#4361ee', border: '1.5px solid #bfdbfe', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.target.style.background = '#4361ee'; e.target.style.color = '#fff'; e.target.style.borderColor = '#4361ee'; }}
                        onMouseLeave={e => { e.target.style.background = '#fff';    e.target.style.color = '#4361ee'; e.target.style.borderColor = '#bfdbfe'; }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px 20px', gap: 6, borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, background: page === 1 ? '#f7fafc' : '#fff', color: page === 1 ? '#cbd5e0' : '#4a5568', cursor: page === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 500 }}>
              <ChevronLeft size={15} /> Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '...'
                  ? <span key={`e${i}`} style={{ padding: '7px 4px', color: '#9ca3af', fontSize: 13 }}>···</span>
                  : <button key={p} onClick={() => setPage(p)}
                      style={{ width: 36, height: 36, border: '1.5px solid', borderRadius: 7, fontWeight: page === p ? 700 : 500, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s', borderColor: page === p ? '#4361ee' : '#e2e8f0', background: page === p ? '#4361ee' : '#fff', color: page === p ? '#fff' : '#4a5568' }}>
                      {p}
                    </button>
              )
            }

            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, background: page === totalPages ? '#f7fafc' : '#fff', color: page === totalPages ? '#cbd5e0' : '#4a5568', cursor: page === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 500 }}>
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>

      {/* ── Order Detail Modal ── */}
      <OrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          div[style*="repeat(4,1fr)"] { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 500px) {
          div[style*="repeat(4,1fr)"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default OrdersPage;